/**
 * Your Feed (OutlierOS /feed replica, docs/legacy-ui/01 §2): the user's scanned and saved creators.
 * Onboarding card → header card (h1 + pill, Export Feed, counts, Analyze @handle) → toolbar (search, bulk remove,
 * Grid/List, Sort) → tag chips → creator cards / list table. Upgrades: tags can be removed, bulk remove uses a
 * ConfirmDialog, and the feed export is a proper CSV.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { BarChart2, ChevronDown, Compass, Download, LayoutGrid, List, Plus, Rocket, Search, Trash2, X } from 'lucide-react';
import { toCsv } from '@vw/core/src/csv';
import { Button, ConfirmDialog, EmptyState, HeaderCard, Input, Modal, Pill, Segmented, Select, Spinner, StatChip, toast } from '@vw/ui';
import { api, ApiError, type SavedCreator } from '../api';
import { AnalyzeButton, CreatorCardView, CreatorCell, CreatorTable } from '../components/CreatorGrid';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { downloadText, stamp } from '../download';
import { compact, igReelsTab, mult } from '../format';
import { peakOf, sortCreators, usualViewsOf, type CreatorSort } from '../creator-sort';
import { plural } from '@vw/core/src/plural';
import { Link, navigate } from '../router';
import { useSession } from '../session';

type Sort = CreatorSort;
const SORTS: Array<{ value: Sort; label: string }> = [
  { value: 'recent', label: 'Recently Scanned' },
  { value: 'peak', label: 'Highest Wise Score' },
  { value: 'reels', label: 'Total Reels' },
  { value: 'views', label: 'Usual Views' },
  { value: 'transcripts', label: 'Free Transcripts' },
  { value: 'name', label: 'Creator Name' },
];


export function Feed() {
  const { library, reloadLibrary } = useSession();
  const [view, setView] = useState<'grid' | 'list'>(() => { try { return localStorage.getItem('vw_feed_view') === 'list' ? 'list' : 'grid'; } catch { return 'grid'; } });
  const [sort, setSort] = useState<Sort>('recent');
  const [q, setQ] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{ pks: string[]; label: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [tagFor, setTagFor] = useState<SavedCreator | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set()); // optimistic removals

  const creators = useMemo(() => (library ?? []).filter((c): c is SavedCreator => !c.unavailable && !!c.handle && !hidden.has(c.igPk)), [library, hidden]);
  // A selection never outlives a filter change, so bulk remove only touches creators on screen.
  useEffect(() => setSelected(new Set()), [q, tag]);
  const unavailable = (library ?? []).filter((c) => c.unavailable).length;
  const allTags = useMemo(() => [...new Set(creators.flatMap((c) => c.tags))].sort(), [creators]);
  // Removing the last creator's copy of the tag being filtered on would leave an empty feed with no chip to reset it.
  useEffect(() => { if (tag && !allTags.includes(tag)) setTag(null); }, [tag, allTags]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^[@#]/, '');
    const filtered = creators.filter((c) => (!tag || c.tags.includes(tag))
      && (!s || (c.handle ?? '').includes(s) || (c.fullName ?? '').toLowerCase().includes(s) || c.tags.some((t) => t.includes(s))));
    return sortCreators(filtered, sort);
  }, [creators, q, tag, sort]);
  const totalReels = creators.reduce((n, c) => n + c.reelCount, 0);

  const setViewSaved = (v: 'grid' | 'list') => { setView(v); try { localStorage.setItem('vw_feed_view', v); } catch { /* ignore */ } };
  const toggle = (pk: string) => { const n = new Set(selected); n.has(pk) ? n.delete(pk) : n.add(pk); setSelected(n); };

  async function remove(pks: string[]) {
    setBusy(true);
    try {
      for (let i = 0; i < pks.length; i += 100) await api('/api/library/remove', { method: 'POST', json: { igPks: pks.slice(i, i + 100) } });
      setHidden(new Set([...hidden, ...pks]));
      setSelected(new Set());
      toast.success(pks.length === 1 ? 'Removed from your feed' : `Removed ${plural(pks.length, 'creator')} from your feed`, { description: 'You can scan them again any time.' });
      reloadLibrary();
    } catch (e) {
      toast.error('Could not remove creators', { description: (e as Error).message });
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  function exportFeed() {
    const rows = shown.map((c) => { const peak = peakOf(c); const usual = usualViewsOf(c); return [c.handle ?? '', c.fullName ?? '', 'Instagram', c.reelCount, peak !== null ? Number(peak.toFixed(2)) : '', usual !== null ? Math.round(usual) : '', c.followers ?? '', c.tags.join(' '), c.transcriptCount ?? 0]; });
    downloadText(`your-feed-${stamp()}.csv`, toCsv(['Handle', 'Name', 'Platform', 'Total Reels', 'Peak Wise Score', 'Usual Views', 'Followers', 'Tags', 'Free Transcripts'], rows), 'text/csv');
  }

  if (!library) return <Spinner label="Loading your feed…" />;

  return (
    <div className="mx-auto max-w-[1600px] space-y-6">
      <OnboardingChecklist variant="card" />
      <HeaderCard>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Your Feed</h1>
              <Pill tone="primary">Scanned Profiles</Pill>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Creators you have scanned with the extension or saved from the Creator Library.</p>
            <AnalyzeBox />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={exportFeed} disabled={!shown.length}><Download className="h-3.5 w-3.5" />Export Feed</Button>
            <StatChip value={creators.length} label="Creators" />
            <StatChip value={compact(totalReels)} label="Reels" />
          </div>
        </div>
        {creators.length > 0 && (
          <>
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <Input icon={<Search className="h-4 w-4" />} className="flex-1" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your scanned creators by handle, name, or #tag…" aria-label="Search your feed" />
              {selected.size > 0 && (
                <div className="flex items-center gap-1">
                  <Button variant="danger" onClick={() => setConfirm({ pks: [...selected], label: plural(selected.size, 'creator') })}><Trash2 className="h-3.5 w-3.5" />Remove {selected.size} from library</Button>
                  <button aria-label="Clear selection" onClick={() => setSelected(new Set())} className="rounded-lg p-2 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>
                </div>
              )}
              <Segmented value={view} onChange={setViewSaved} options={[{ value: 'grid', label: <><LayoutGrid className="h-3.5 w-3.5" />Grid</> }, { value: 'list', label: <><List className="h-3.5 w-3.5" />List</> }]} />
              <label className="relative flex items-center">
                <span className="sr-only">Sort</span>
                <Select value={sort} onChange={(e) => setSort(e.target.value as Sort)} className="appearance-none pr-8">{SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</Select>
                <ChevronDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-muted-foreground" />
              </label>
            </div>
            {allTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tags:</span>
                {[null, ...allTags].map((t) => (
                  <button key={t ?? '*'} onClick={() => setTag(t)} className={tag === t ? 'rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground' : 'rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground'}>{t ? `#${t}` : 'All'}</button>
                ))}
              </div>
            )}
          </>
        )}
      </HeaderCard>

      {!creators.length ? (
        <EmptyState icon={<BarChart2 className="h-7 w-7" />} title="Your feed is empty"
          actions={<Button onClick={() => navigate('/explore')}><Compass className="h-3.5 w-3.5" />Open Creator Library</Button>}>
          Your feed shows the creators you scan. Open a creator's Reels tab on Instagram and press Scan in the extension, or browse the Creator Library.
        </EmptyState>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {shown.map((c) => (
            <CreatorCardView key={c.igPk} c={c} selected={selected.has(c.igPk)} selecting={selected.size > 0} onSelect={() => toggle(c.igPk)}
              footerExtra={<button aria-label="Remove from your feed" title="Remove from your feed" onClick={() => setConfirm({ pks: [c.igPk], label: `@${c.handle}` })} className="rounded-xl p-2 text-muted-foreground hover:bg-secondary hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>}>
              <TagRow c={c} onAdd={() => setTagFor(c)} onChange={reloadLibrary} />
            </CreatorCardView>
          ))}
        </div>
      ) : (
        <CreatorTable rows={shown} columns={[
          { id: 'sel', label: <input type="checkbox" aria-label="Select all" checked={shown.length > 0 && shown.every((c) => selected.has(c.igPk))} onChange={(e) => setSelected(e.target.checked ? new Set(shown.map((c) => c.igPk)) : new Set())} />, cell: (c) => <input type="checkbox" aria-label={`Select @${c.handle}`} checked={selected.has(c.igPk)} onChange={() => toggle(c.igPk)} />, className: 'w-10' },
          { id: 'creator', label: 'Creator', cell: (c) => <CreatorCell c={c} /> },
          { id: 'platform', label: 'Platform', cell: () => <span className="rounded bg-secondary px-1.5 text-[9px] font-bold uppercase tracking-wider">Instagram</span> },
          { id: 'reels', label: 'Reels Count', cell: (c) => <span className="tabular-nums">{c.reelCount}</span> },
          { id: 'peak', label: 'Peak Wise Score', cell: (c) => <span className="font-bold tabular-nums text-amber-600 dark:text-amber-400">{mult(peakOf(c))}</span> },
          { id: 'views', label: 'Usual Views', cell: (c) => <span className="tabular-nums text-emerald-600 dark:text-emerald-400">{compact(usualViewsOf(c))}</span> },
          { id: 'tags', label: 'Tags', cell: (c) => <span className="text-xs text-muted-foreground">{c.tags.map((t) => `#${t}`).join(' ') || '–'}</span> },
          { id: 'act', label: 'Actions', cell: (c) => <AnalyzeButton handle={c.handle} />, className: 'text-right' },
        ]} />
      )}
      {unavailable > 0 && <p className="text-xs text-muted-foreground">{plural(unavailable, 'saved creator')} {unavailable === 1 ? 'is' : 'are'} no longer available.</p>}

      <ConfirmDialog open={!!confirm} title={`Remove ${confirm?.label ?? ''} from your feed?`} body={<p>You can scan them again any time. Their reels stay in {confirm && confirm.pks.length > 1 ? 'their reports' : 'the report'}.</p>}
        confirmLabel="Remove" danger busy={busy} onCancel={() => setConfirm(null)} onConfirm={() => confirm && remove(confirm.pks)} />
      <TagModal creator={tagFor} onClose={() => setTagFor(null)} onSaved={() => { setTagFor(null); reloadLibrary(); }} />
    </div>
  );
}

function TagRow({ c, onAdd, onChange }: { c: SavedCreator; onAdd: () => void; onChange: () => void }) {
  async function removeTag(t: string) {
    try {
      await api(`/api/library/${c.igPk}/tags`, { method: 'PUT', json: { tags: c.tags.filter((x) => x !== t) } });
      onChange();
    } catch (e) {
      toast.error('Could not remove the tag', { description: (e as Error).message });
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button onClick={onAdd} className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground hover:border-primary hover:text-primary"><Plus className="h-3 w-3" />Tag</button>
      {c.tags.map((t) => (
        <span key={t} className="group/tag inline-flex items-center gap-0.5 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
          #{t}<button aria-label={`Remove tag ${t}`} onClick={() => removeTag(t)} className="opacity-60 hover:text-rose-500 hover:opacity-100"><X className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
}

function TagModal({ creator, onClose, onSaved }: { creator: SavedCreator | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (!creator || !name.trim()) return;
    setBusy(true);
    try {
      await api(`/api/library/${creator.igPk}/tags`, { method: 'PUT', json: { tags: [...creator.tags, ...name.split(/[,\s]+/).filter(Boolean)] } });
      setName('');
      onSaved();
    } catch (err) {
      toast.error('Could not save the tag', { description: err instanceof ApiError ? err.message : undefined });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal open={!!creator} onClose={onClose} title={`Add Tag to @${creator?.handle ?? ''}`} labelledBy="tag-title"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!name.trim()} onClick={() => save()}>Save Tag</Button></>}>
      <form onSubmit={save}>
        <label className="text-xs font-semibold">Tag Name
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="e.g. saas (separate several with commas)"
            className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        </label>
      </form>
    </Modal>
  );
}

/** "Analyze @handle" (ViralWise addition): opens a creator's report, or explains how to scan them. */
function AnalyzeBox() {
  const [q, setQ] = useState('');
  const [missing, setMissing] = useState<string | null>(null);
  async function go(e: FormEvent) {
    e.preventDefault();
    const handle = q.trim().replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\//, '').split(/[/?#]/)[0]!.toLowerCase();
    if (!/^[a-z0-9._]{1,30}$/.test(handle)) return toast.info('Enter an Instagram handle, like @hormozi');
    try {
      await api(`/api/creators/lookup?handle=${encodeURIComponent(handle)}`);
      navigate(`/c/${handle}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setMissing(handle);
      else toast.error((err as Error).message);
    }
  }
  return (
    <div className="mt-4 max-w-lg">
      <form onSubmit={go} className="flex gap-2">
        <Input icon={<Rocket className="h-4 w-4" />} className="flex-1" value={q} onChange={(e) => { setQ(e.target.value); setMissing(null); }} placeholder="Analyze @handle or paste an instagram.com link" aria-label="Analyze a creator" />
        <Button type="submit">Analyze</Button>
      </form>
      {missing && (
        <p className="mt-2 text-xs text-muted-foreground">
          <b className="text-foreground">@{missing}</b> isn't in your feed yet. <a href={igReelsTab(missing)} target="_blank" rel="noreferrer" className="font-semibold text-primary">Open their Reels tab</a> and press Scan in the extension, or check the <Link to="/explore" className="font-semibold text-primary">Creator Library</Link>.
        </p>
      )}
    </div>
  );
}
